/**
 * PreferencesProvider — manages the full Preferences shape independently of ThemeProvider.
 *
 * Decision (B): separate provider wraps around ThemeProvider; ThemeProvider continues to
 * manage themeMode (via qm-theme localStorage key + .dark class). PreferencesProvider
 * observes ThemeProvider's `theme` value and keeps prefs.themeMode in sync (read direction
 * only). When the user explicitly sets themeMode via Preferences UI, setPref calls
 * useTheme().setTheme() to push the change into ThemeProvider (write direction on demand).
 *
 * This bidirectional pattern avoids the mount-time clobber bug: on first mount the bridge
 * does NOT blindly call setTheme(prefs.themeMode) — it reads ThemeProvider's already-resolved
 * theme and seeds prefs.themeMode from it, so any theme the user set via the dropdown is
 * preserved even if qm.prefs is absent or stale.
 *
 * HTML data-attribute sync (all on every change via useEffect):
 *   data-bn-typography  ← prefs.bnTypography
 *   data-bn-numerals    ← "on" | "off"
 *   data-bilingual-labels ← "on" | "off"
 *   data-density        ← prefs.density
 *   data-accent         ← prefs.accent
 *
 * FOUC mitigation: state initialised from localStorage synchronously in useState()
 * (same pattern as ThemeProvider). Vite SPA — no hydration mismatch risk.
 */
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {
  clearPreferences,
  DEFAULT_PREFERENCES,
  getStoredPreferences,
  type Preferences,
  storePreferences,
} from './preferences';
import { useTheme } from './theme-provider';

type PreferencesContextValue = {
  prefs: Preferences;
  /** Set a single preference key immediately (writes localStorage + updates DOM). */
  setPref: <K extends keyof Preferences>(key: K, value: Preferences[K]) => void;
  /** Reset all preferences to defaults (clears localStorage). */
  reset: () => void;
};

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

/**
 * Inner provider that has access to useTheme() (must be mounted inside ThemeProvider).
 * Observes ThemeProvider.theme to keep prefs.themeMode in sync (read direction on mount
 * and external changes). Writes ThemeProvider.setTheme() only when user explicitly calls
 * setPref('themeMode', ...) or reset().
 */
function PreferencesInner({ children }: { children: ReactNode }) {
  const { theme: themeProviderTheme, setTheme } = useTheme();

  // Seed initial prefs from storage, but override themeMode with ThemeProvider's authoritative
  // value so we don't clobber a theme the user set via the dropdown (qm-theme key).
  const [prefs, setPrefs] = useState<Preferences>(() => {
    const stored = getStoredPreferences();
    // themeProviderTheme is already resolved from qm-theme localStorage at ThemeProvider mount.
    // Overwrite whatever qm.prefs says so ThemeProvider is always the authority for themeMode.
    return { ...stored, themeMode: themeProviderTheme };
  });

  // isInternalThemeChange: true while we're writing setTheme() ourselves so the observer
  // below doesn't echo it back and cause an infinite loop.
  const isInternalThemeChange = useRef(false);

  // Sync HTML data attributes on every prefs change
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-bn-typography', prefs.bnTypography);
    html.setAttribute('data-bn-numerals', prefs.bnNumerals ? 'on' : 'off');
    html.setAttribute('data-bilingual-labels', prefs.bilingualLabels ? 'on' : 'off');
    html.setAttribute('data-density', prefs.density);
    html.setAttribute('data-accent', prefs.accent);
    // Note: CSS rules for density/accent/mixed typography are deferred to a follow-up TODO.
    // The attributes are set now for forward-compatibility; only bnNumerals has an active
    // CSS consumer (via <Num> atom). data-bn-typography="siliguri|serif" has active
    // CSS rules in globals.css ([data-bn-typography]); "mixed" is deferred.
  }, [prefs]);

  // Observe ThemeProvider's theme (e.g. changed via dropdown's ThemeToggle) and keep
  // prefs.themeMode in sync. This is read-direction only; does NOT call setTheme().
  useEffect(() => {
    if (isInternalThemeChange.current) {
      isInternalThemeChange.current = false;
      return;
    }
    // External change (ThemeToggle or system preference) — update prefs to match.
    setPrefs((prev) => {
      if (prev.themeMode === themeProviderTheme) return prev; // no-op if already in sync
      const next = { ...prev, themeMode: themeProviderTheme };
      storePreferences(next);
      return next;
    });
  }, [themeProviderTheme]);

  const setPref = <K extends keyof Preferences>(key: K, value: Preferences[K]) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      storePreferences(next);
      return next;
    });
    // If themeMode is changing, propagate to ThemeProvider (write direction).
    if (key === 'themeMode') {
      isInternalThemeChange.current = true;
      setTheme(value as Preferences['themeMode']);
    }
  };

  const reset = () => {
    clearPreferences();
    isInternalThemeChange.current = true;
    setTheme(DEFAULT_PREFERENCES.themeMode);
    setPrefs({ ...DEFAULT_PREFERENCES });
  };

  return (
    <PreferencesContext.Provider value={{ prefs, setPref, reset }}>
      {children}
    </PreferencesContext.Provider>
  );
}

// Split kept intentionally: PreferencesInner calls useTheme() which requires
// ThemeProvider to already be mounted above it. Keeping Inner as a named
// component also gives React a cleaner error frame when the context is missing.
// Do not collapse into a single function.
/**
 * Mount in __root.tsx, inside ThemeProvider so PreferencesInner can useTheme().
 */
export function PreferencesProvider({ children }: { children: ReactNode }) {
  return <PreferencesInner>{children}</PreferencesInner>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}

/** Resilient variant — returns null if provider isn't mounted (HMR / boundary edge cases). Use in shell components that must not crash. */
export function usePreferencesSafe(): PreferencesContextValue | null {
  return useContext(PreferencesContext);
}
